import { fetcher, fetcherConfig } from '../../lib/api-fetcher';
import type {
  CustomerListItem,
  CustomerDetail,
  SubscriptionListItem,
  ReceiptListItem,
  PaginatedResponse,
  CreateCustomerPayload,
  UpdateCustomerPayload,
  Customer360Detail,
  CustomerBankItem,
  CustomerNomineeItem,
  CustomerEducationItem,
  CustomerOccupationItem,
  CustomerIntroducerItem,
  CustomerDocumentItem,
  CustomerContactItem,
  CustomerAddressItem,
} from './types';



const BASE = '/staff/v1/customers';

export function listCustomers(params: {
  page: number;
  limit: number;
  query?: string;
}): Promise<PaginatedResponse<CustomerListItem>> {
  const qs = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    ...(params.query ? { q: params.query } : {}),
  });
  return fetcher.get<PaginatedResponse<CustomerListItem>>(`${BASE}?${qs}`);
}

export function createCustomer(body: CreateCustomerPayload): Promise<CustomerDetail> {
  return fetcher.post<CustomerDetail>(BASE, body);
}

export function getCustomer(id: string): Promise<CustomerDetail> {
  return fetcher.get<CustomerDetail>(`${BASE}/${id}`);
}

export function updateCustomer(id: string, body: UpdateCustomerPayload): Promise<CustomerDetail> {
  return fetcher.patch<CustomerDetail>(`${BASE}/${id}`, body);
}

export function blacklistCustomer(id: string, reason: string): Promise<CustomerDetail> {
  return fetcher.post<CustomerDetail>(`${BASE}/${id}/blacklist`, { reason });
}

export function markCustomerDeceased(
  id: string,
  payload: { deathDate?: string; deathCertificateUrl?: string }
): Promise<Record<string, unknown>> {
  return fetcher.post<Record<string, unknown>>(`${BASE}/${id}/deceased`, payload);
}

export function listCustomerSubscriptions(
  id: string,
  params: { page: number; limit: number },
): Promise<PaginatedResponse<SubscriptionListItem>> {
  const qs = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
  return fetcher.get<PaginatedResponse<SubscriptionListItem>>(`${BASE}/${id}/subscriptions?${qs}`);
}

export function listCustomerReceipts(
  id: string,
  params: { page: number; limit: number },
): Promise<PaginatedResponse<ReceiptListItem>> {
  const qs = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
  return fetcher.get<PaginatedResponse<ReceiptListItem>>(`${BASE}/${id}/receipts?${qs}`);
}

export function checkMobileDuplicate(mobile: string): Promise<{ exists: boolean; customerId: string | null }> {
  return fetcher.get<{ exists: boolean; customerId: string | null }>(`${BASE}/check-duplicate?mobile=${encodeURIComponent(mobile)}`);
}

export function enableMobileAccess(id: string): Promise<{ success: boolean }> {
  return fetcher.post<{ success: boolean }>(`${BASE}/${id}/enable-mobile-access`, {});
}

export function getCustomer360(id: string): Promise<Customer360Detail> {
  return fetcher.get<Customer360Detail>(`${BASE}/${id}/360`);
}

export interface KycDocument {
  id: string;
  kycId: string;
  docType: 'PAN' | 'AADHAAR' | 'PASSPORT' | 'VOTER_ID' | 'DRIVING_LICENSE' | 'PHOTO';
  fileRef: string;
  fileHash: string;
  docNumber: string | null;
  maskedDocNumber: string | null;
  capturedBy: string;
}

export interface KycStatusResponse {
  id: string | null;
  status: 'PENDING' | 'COMPLETED' | 'REJECTED' | null;
  rejectedReason: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  documents: KycDocument[];
}

export function getKycStatus(memberId: string): Promise<KycStatusResponse> {
  return fetcher.get<KycStatusResponse>(`/staff/v1/kyc/customer/${memberId}`);
}

export function startKyc(memberId: string): Promise<{ id: string; status: string }> {
  return fetcher.post<{ id: string; status: string }>(`/v1/kyc/customer/${memberId}/start`, {});
}

export function deleteKycDocument(docId: string): Promise<{ success: boolean }> {
  return fetcher.delete<{ success: boolean }>(`/v1/kyc/kyc-documents/${docId}`);
}

export function submitKyc(kycId: string): Promise<Record<string, unknown>> {
  return fetcher.post<Record<string, unknown>>(`/v1/kyc/${kycId}/submit`, {});
}

export function verifyKyc(kycId: string): Promise<{ success: boolean }> {
  return fetcher.post<{ success: boolean }>(`/staff/v1/kyc/${kycId}/verify`, {});
}

export function rejectKyc(kycId: string, reason: string): Promise<{ success: boolean }> {
  return fetcher.post<{ success: boolean }>(`/staff/v1/kyc/${kycId}/reject`, { reason });
}

/**
 * All 5 customer child entities (banks/nominees/education/occupation/introducers) share the
 * exact same list/create/delete shape server-side (see customer-sub-entities.controller.ts) --
 * genuinely identical across every instance, so one factory instead of 5 copies.
 */
function createSubEntityApi<TItem, TCreate>(segment: string) {
  return {
    list: (customerId: string): Promise<TItem[]> =>
      fetcher.get<TItem[]>(`${BASE}/${customerId}/${segment}`),
    create: (customerId: string, body: TCreate): Promise<TItem> =>
      fetcher.post<TItem>(`${BASE}/${customerId}/${segment}`, body as Record<string, unknown>),
    remove: (customerId: string, id: string): Promise<{ success: boolean }> =>
      fetcher.delete<{ success: boolean }>(`${BASE}/${customerId}/${segment}/${id}`),
  };
}

export const customerBanksApi = createSubEntityApi<CustomerBankItem, {
  bankName: string; accountType?: 'SAVINGS' | 'CURRENT'; accountNumber: string; ifscCode: string; isPrimary?: boolean;
}>('banks');

export const customerNomineesApi = createSubEntityApi<CustomerNomineeItem, {
  name: string; relation?: string; dob?: string; percentage?: number; photoUrl?: string;
}>('nominees');

export const customerEducationApi = createSubEntityApi<CustomerEducationItem, {
  education: string; spouseEducation?: string;
}>('education');

export const customerOccupationApi = createSubEntityApi<CustomerOccupationItem, {
  currentEmployerName?: string; monthlyIncome?: number; noOfYears?: number; occupationName?: string; rentedOwnHouse?: 'RENTED' | 'OWN'; yearsCurrentResidence?: number;
}>('occupation');

export const customerIntroducersApi = createSubEntityApi<CustomerIntroducerItem, {
  introducerCustomerId?: string; introducerName?: string; introType?: string; relationshipWithToMember?: string; phoneNo?: string; email?: string; occupation?: string; addressLine1?: string; city?: string; state?: string; country?: string; comments?: string; otherInfo?: string;
}>('introducers');

export const customerContactsApi = createSubEntityApi<CustomerContactItem, {
  type: 'PHONE' | 'EMAIL'; value: string; isPrimary?: boolean;
}>('contacts');

export const customerAddressesApi = createSubEntityApi<CustomerAddressItem, {
  addressType?: 'RESIDENTIAL' | 'OFFICE' | 'PERMANENT' | 'OTHER'; addressLine1: string; addressLine2?: string; city: string; stateCode: string; pincode: string; countryCode?: string; isPrimary?: boolean;
}>('addresses');

export function listCustomerDocuments(customerId: string, types?: string): Promise<CustomerDocumentItem[]> {
  const qs = types ? `?types=${encodeURIComponent(types)}` : '';
  return fetcher.get<CustomerDocumentItem[]>(`${BASE}/${customerId}/documents${qs}`);
}

export function deleteCustomerDocument(customerId: string, documentId: string): Promise<{ success: boolean }> {
  return fetcher.delete<{ success: boolean }>(`${BASE}/${customerId}/documents/${documentId}`);
}

// The customers-controller document upload route takes a raw multipart body (file + docType +
// optional docNumber), same shape as the KYC upload flow in CustomerProfilePage.tsx -- the plain
// JSON `fetcher` client doesn't support multipart, so this goes through the browser fetch API
// directly, mirroring that same pattern.
export async function uploadCustomerDocument(
  customerId: string,
  file: File,
  docType: string,
  docNumber?: string
): Promise<{ fileId: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('docType', docType);
  if (docNumber) {
    formData.append('docNumber', docNumber);
  }

  const token = fetcherConfig.getToken?.();
  const orgUnitId = fetcherConfig.getOrgUnitId?.();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (orgUnitId) headers['x-org-unit-id'] = orgUnitId;

  const response = await fetch(`${fetcherConfig.baseUrl}/staff/v1/customers/${customerId}/documents`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    throw new Error(json.message || 'Upload failed');
  }

  return response.json();
}

// KYC is a shared, surface-agnostic endpoint (no /staff prefix on the backend -- see
// KycController @Controller({ path: 'kyc', version: '1' })), same multipart-upload shape as
// uploadCustomerDocument above.
export async function uploadKycDocument(
  memberId: string,
  file: File,
  docType: string,
  docNumber?: string
): Promise<{ fileId: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('docType', docType);
  if (docNumber) {
    formData.append('docNumber', docNumber);
  }

  const token = fetcherConfig.getToken?.();
  const orgUnitId = fetcherConfig.getOrgUnitId?.();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (orgUnitId) headers['x-org-unit-id'] = orgUnitId;

  const response = await fetch(`${fetcherConfig.baseUrl}/v1/kyc/member/${memberId}/documents`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    throw new Error(json.message || 'Upload failed');
  }

  return response.json();
}

export interface KycDocumentType {
  id: string;
  code: string;
  label: string;
  validationPattern: string | null;
}

export function getKycDocumentTypes(): Promise<KycDocumentType[]> {
  return fetcher.get<KycDocumentType[]>(`/v1/kyc/document-types`);
}
